/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {defaultsDeep, get, isPlainObject, set} from 'lodash';
import objectHash from 'object-hash';

/**
 * @stability Experimental
 */
export class AbstractService {
  /**
   * @abstract
   *
   * @returns {string}
   */
  static get name() {
    throw new Error('Cannot access abstract property `AbstractService.id`.');
  }

  /**
   * @returns {object}
   */
  static get profile() {
    return {
      baseUrl: '',
      options: {
        cache: 'default',
        credentials: 'same-origin',
        method: 'GET',
        redirect: 'follow',
        headers: {
          'accept': 'application/json',
          'x-requested-with': 'XMLHttpRequest',
        },
      },
    };
  }

  /**
   * List of variable names that used in the `profile` object
   *
   * @private
   *
   * @returns {string[]}
   */
  static get variables() {
    return [];
  }

  /**
   * @private
   *
   * @type {Map<string, AbstractService>}
   */
  static instances = new Map();

  /**
   * Gets or create the singleton instance
   *
   * @param {object} variables
   *
   * @returns {AbstractService}
   */
  static instantiate({origin, token, ...variables}) {
    const key = [this.name, objectHash.MD5(variables)].join('|');

    if (!this.instances.has(key)) {
      this.instances.set(key, new this(key, {origin, token, ...variables}));
    }

    return this.instances.get(key);
  }

  /**
   * Unique key for this service instance
   *
   * @type {string}
   */
  key = null;

  /**
   * @protected
   *
   * @type {string}
   */
  baseUrl = null;

  /**
   * @protected
   *
   * @type {object}
   */
  options = null;

  /**
   * @protected
   *
   * @type {object}
   */
  variables = null;

  /**
   * Constructor
   *
   * @param {string} key
   * @param {object} variables
   */
  constructor(key, variables = {}) {
    if (new.target === AbstractService) {
      throw new Error('Cannot instantiate abstract class `Service`');
    }

    const {dynamicProperties, ...originProfile} = this.constructor.profile;
    const {baseUrl, options = {}, ...profile} = this.evaluateVariables(originProfile, variables, dynamicProperties);

    this.key = key;
    this.baseUrl = baseUrl?.replace(/\/+$/, '');
    this.options = options;
    this.variables = variables;

    for (const [key, value] of Object.entries(profile)) {
      this[key] = value;
    }
  }

  /**
   * @param {string}      url     Absolute or relative URL of the resource that is being requested.
   * @param {RequestInit} options An object containing any custom settings that you want to apply to the request.
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async send(url, options = {}) {
    return this.createRequest(url, options)
      .then(({url, options}) => fetch(url, options))
      .then((response) => this.parseResponse(response))
    ;
  }

  /**
   * Shortcut method to perform `GET` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {RequestInit} options Configuration object
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async get(url, options = {}) {
    return this.send(url, {...options, method: 'get'});
  }

  /**
   * Shortcut method to perform `DELETE` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {RequestInit} options Configuration object
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async delete(url, options = {}) {
    return this.send(url, {...options, method: 'delete'});
  }

  /**
   * Shortcut method to perform `HEAD` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {RequestInit} options Configuration object
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async head(url, options = {}) {
    return this.send(url, {...options, method: 'head'});
  }

  /**
   * Shortcut method to perform `JSONP` request.
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request.
   *                              The name of the callback should be the string `JSON_CALLBACK`
   * @param {any}         data    Request content
   * @param {RequestInit} options Configuration object
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async jsonp(url, options = {}) {
    return this.send(url, {...options, method: 'jsonp'});
  }

  /**
   * Shortcut method to perform `POST` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {any}         data    Request content
   * @param {RequestInit} options Configuration object
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async post(url, data, options = {}) {
    return this.send(url, {...options, body: data, method: 'POST'});
  }

  /**
   * Shortcut method to perform `PUT` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {any}         data    Request content
   * @param {RequestInit} options Configuration object
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async put(url, data, options = {}) {
    return this.send(url, {...options, body: data, method: 'put'});
  }

  /**
   * Shortcut method to perform `PATCH` request
   *
   * @param {string}      url     Relative or absolute URL specifying the destination of the request
   * @param {any}         data    Request content
   * @param {RequestInit} options Configuration object
   *
   * @return {Promise<object | string>} A `Promise` that resolves to the response data
   */
  async patch(url, data, options = {}) {
    return this.send(url, {...options, body: data, method: 'patch'});
  }

  /**
   * @protected
   *
   * @param {string}      url
   * @param {RequestInit} options
   *
   * @returns {Promise<{url: string, options: RequestInit & {parameters: URLSearchParams}}>}
   */
  async createRequest(url, {parameters, body, ...options} = {}) {
    defaultsDeep(options, this.options);

    if (typeof url === 'string') {
      url = this.evaluateVariables(url, this.variables);

      url = `${this.baseUrl}/${url.replace(/^\/+/, '')}`;
    }

    if (parameters) {
      if (typeof parameters === 'object' && !(parameters instanceof URLSearchParams)) {
        parameters = new URLSearchParams(parameters).toString();
      }

      url += (url.includes('?') ? '&' : '?') + parameters;
    }

    if (body && typeof body === 'object') {
      const contentType = options.headers?.['content-type'] || options.headers?.['Content-Type'];

      if (contentType?.includes('json')) {
        options.body = JSON.stringify(body);
      }
    }

    return Promise.resolve({url, options});
  }

  /**
   * @protected
   *
   * @param {Response} response
   *
   * @returns {Promise<object | string>}
   */
  async parseResponse(response) {
    const contentType = response.headers.get('content-type');
    let data;

    if (contentType?.includes('json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    return response.ok ? Promise.resolve(data) : Promise.reject(data);
  }

  /**
   * @protected
   *
   * @param {string|object} inputData
   * @param {object}        variables
   * @param {string[]}      [properties]
   *
   * @returns {object}
   */
  evaluateVariables(inputData, variables, properties = []) {
    if (typeof inputData === 'string') {
      const variablesRegex = new RegExp('{(.*?)}', 'g');
      const variableReplacer = (match, variable) => get(variables, variable.trim(), match);

      inputData = inputData.replace(variablesRegex, variableReplacer);
    } else if (isPlainObject(inputData) && properties.length > 0) {
      for (const property of properties) {
        const value = get(inputData, property);

        if (typeof value === 'string') {
          set(inputData, property, this.evaluateVariables(value, variables));
        }
      }
    }

    return inputData;
  }
}
